import moment from 'moment';
import * as XLSX from 'xlsx';

export const exportToExcel = (
  completedBookings,
  venue,
  multiplyValueForCurrency,
  categoryMap
) => {
  if (!completedBookings || completedBookings.length === 0) return;

  const currencyMultiplier = multiplyValueForCurrency(venue.currency);

  const toNumber = v => Number(v || 0);
  const formatValue = v => v / currencyMultiplier;

  // const getProfit = (unitSale, unitCost, qty) => {
  //   if (!unitCost || unitCost > unitSale) return null;
  //   return (unitSale - unitCost) * qty;
  // };

  const rows = [];

  let grandTotalCommission = 0;
  let grandTotalRefunds = 0;
  let grandTotalPaid = 0;

  completedBookings.forEach((booking, bookingIndex) => {
    const bookingId = booking.id || `Order-${bookingIndex}`;
    const bookingDate = booking.paid_at
      ? moment(booking.paid_at).format('DD/MM/YYYY')
      : '';
    const orderType =
      booking.service_type === 'table' ? 'eat-in' : booking.service_type;

    const tipRaw = toNumber(booking.tip_amount);
    const commissionRaw = toNumber(booking.customer_service_fee_paid);
    const refundedRaw = toNumber(booking.refunded);

    const totalPaidRaw = toNumber(booking.total);
    const finalPayoutRaw = totalPaidRaw - commissionRaw - refundedRaw;

    grandTotalCommission += commissionRaw;
    grandTotalRefunds += refundedRaw;
    grandTotalPaid += totalPaidRaw;

    let isFirstRow = true;

    (booking.order_items || []).forEach((item, itemIndex) => {
      const qty = toNumber(item.quantity);
      const unitSale = toNumber(item.productPrice);
      const cost = toNumber(item.costPrice);
      const totalCost = cost * qty;

      const itemVatRate = toNumber(item.vatRate);
      const itemTotalSale = unitSale * qty;
      const itemNet =
        venue.enable_vat && itemVatRate > 0
          ? Math.round(itemTotalSale / (1 + itemVatRate))
          : itemTotalSale;
      const itemVat = itemTotalSale - itemNet;

      rows.push({
        'Customer Name': isFirstRow ? booking.name || '' : '',
        Date: bookingDate,
        'Order Number': bookingId,
        Category:
          (categoryMap && categoryMap.get(String(item.id || item._id || ''))) ||
          '',
        'Order Type': orderType,
        'Line Item Number': itemIndex + 1,
        Item: item.name || '',
        'Total Paid': isFirstRow ? formatValue(totalPaidRaw) : 0,
        'Food/Drink Total': formatValue(itemNet),
        Quantity: qty,
        ...(venue.enable_vat && { VAT: formatValue(itemVat) }),
        ...(venue.enable_vat && {
          'Sub Total': formatValue(itemTotalSale),
        }),
        'Cost Price': formatValue(totalCost),
        // Profit: rawProfit !== null ? formatValue(rawProfit) : '',
        ...(venue.tips_enabled && {
          Tips: isFirstRow ? formatValue(tipRaw) : 0,
        }),
        Commission: isFirstRow ? formatValue(commissionRaw) : 0,
        'Final Payout': isFirstRow ? formatValue(finalPayoutRaw) : 0,
      });

      isFirstRow = false;

      (item.children || []).forEach(variant => {
        const vQty = toNumber(variant.quantity || 1) * qty;
        const rawVariantTotal = toNumber(variant.totalPrice);
        const fallbackTotal = toNumber(variant.price) * vQty;
        const vTotalSale =
          rawVariantTotal > 0 ? rawVariantTotal * qty : fallbackTotal;
        const vCost = toNumber(variant.costPrice);
        // const vRawProfit = getProfit(vUnitSale, vCost, vQty);
        const vVatRate = toNumber(variant.vatRate);
        const vNet =
          venue.enable_vat && vVatRate > 0
            ? Math.round(vTotalSale / (1 + vVatRate))
            : vTotalSale;
        const vVat = vTotalSale - vNet;

        rows.push({
          'Customer Name': '',
          Date: bookingDate,
          'Order Number': bookingId,
          Category: '',
          'Order Type': orderType,
          'Line Item Number': `${itemIndex + 1}.${variant.name || ''}`,
          Item: `   └── ${variant.name || ''}`,
          'Total Paid': 0,
          'Food/Drink Total': formatValue(vNet),
          Quantity: vQty,
          ...(venue.enable_vat && { VAT: formatValue(vVat) }),
          ...(venue.enable_vat && {
            'Sub Total': formatValue(vTotalSale),
          }),
          'Cost Price': formatValue(vCost * vQty),
          //Profit: vRawProfit !== null ? formatValue(vRawProfit) : '',
          ...(venue.tips_enabled && { Tips: 0 }),
          Commission: 0,
          'Final Payout': 0,
        });
      });
    });
  });

  rows.push({});
  rows.push({});

  const summaryStartIndex = rows.length;

  const netFinalPayout =
    grandTotalPaid - grandTotalCommission - grandTotalRefunds;

  rows.push({ Item: '===== GRAND SUMMARY =====' });
  rows.push({ Item: 'Total Paid', 'Total Paid': formatValue(grandTotalPaid) });
  rows.push({
    Item: 'Less: Commission (Tabb fee)',
    Commission: formatValue(grandTotalCommission),
  });
  if (grandTotalRefunds > 0) {
    rows.push({
      Item: 'Less: Refunds',
      'Final Payout': formatValue(grandTotalRefunds),
    });
  }
  rows.push({
    Item: '= Total Final Payout',
    'Final Payout': formatValue(netFinalPayout),
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const range = XLSX.utils.decode_range(worksheet['!ref']);

  // number formatting
  for (let R = range.s.r + 1; R <= range.e.r; ++R) {
    ['H', 'I', 'J', 'K', 'L', 'M'].forEach(col => {
      const cell = worksheet[col + (R + 1)];
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = '0.00';
      }
    });
  }

  // auto column width
  const colWidths = [];
  rows.forEach(row => {
    Object.keys(row).forEach((key, i) => {
      const len = (row[key] || '').toString().length;
      colWidths[i] = Math.max(colWidths[i] || 10, len + 2);
    });
  });

  worksheet['!cols'] = colWidths.map(w => ({ wch: w }));

  const summaryRowStartExcel = summaryStartIndex + 1;
  for (let i = summaryRowStartExcel; i <= rows.length; i++) {
    ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'].forEach(
      col => {
        const cell = worksheet[col + i];
        if (cell) cell.s = { font: { bold: true } };
      }
    );
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

  // download
  XLSX.writeFile(
    workbook,
    `completed-orders-${moment().format('YYYY-MM-DD')}.xlsx`
  );
};



// WEBPACK FOOTER //
// ./src/utils/client/excelExport.js